/**
 * ORB Algo Configuration - Single Source of Truth
 * All stats, URLs, and copy must be referenced from this file.
 * NO hardcoded numbers in JSX components.
 * 
 * Last Updated: October 2026
 * Data Source: NinjaTrader 8 Strategy Analyzer Backtests
 */

// ============================================
// VARIATION STATS (1-YEAR BACKTEST)
// ============================================
export const V1_STATS = {
  name: 'V1',
  fullName: 'Monk Algo ORB V1',
  label: 'Conservative',
  tagline: 'Fewer, pickier trades. Smoother curve.',
  description: 'Choose it when staying far from a drawdown limit matters most; run it at a slightly higher risk %.',
  color: '#8B5CF6', // Purple
  colorLight: '#A78BFA',
  colorDark: '#7C3AED',
  
  // 1-Year Performance (365 days)
  oneYear: {
    totalReturn: 45.60,
    netProfit: 45595.50,
    winRate: 76.85,
    profitFactor: 2.93,
    maxDrawdown: 2.51,
    maxDrawdownDollars: 2614.50,
    totalTrades: 108,
    winningTrades: 83,
    losingTrades: 25,
    grossProfit: 69186.50,
    grossLoss: 23591.00,
    expectancy: 422.18,
    largestProfit: 1000.00,
    largestLoss: 997.50,
    dateRange: 'Oct 2025 – Sep 2026',
  },
  
  // 5-Year Performance
  fiveYear: {
    totalTrades: 514,
    dateRange: 'Dec 2021 – Sep 2026',
  },
  
  // Monte Carlo Pass Rates (50,000 simulations, 4% DD limit)
  monteCarlo: {
    simulations: 50000,
    ddLimit: 4,
    passRates: {
      0.25: { pass: 100.0, avgReturn: 30.86, avgDD: 2.0 },
      0.50: { pass: 99.7, avgReturn: 70.79, avgDD: 3.98 },
      0.75: { pass: 98.7, avgReturn: 122.33, avgDD: 5.93 },
      1.00: { pass: 87.9, avgReturn: 188.66, avgDD: 7.84 },
    },
  },
  
  // Recommended risk levels
  recommendedRisk: 0.75,
  defaultRisk: 0.75,
  
  // Monthly P&L (1-year)
  monthlyPnl: [
    { month: "Oct '25", profit: 2149.50 },
    { month: "Nov '25", profit: 3837.50 },
    { month: "Dec '25", profit: 4335.50 },
    { month: "Jan '26", profit: 7511.00 },
    { month: "Feb '26", profit: 4038.00 },
    { month: "Mar '26", profit: 6046.00 },
    { month: "Apr '26", profit: 4910.50 },
    { month: "May '26", profit: 1414.50 },
    { month: "Jun '26", profit: 4712.50 },
    { month: "Jul '26", profit: 2862.50 },
    { month: "Aug '26", profit: 1523.00 },
    { month: "Sep '26", profit: 2255.00 },
  ],
};

export const V2_STATS = {
  name: 'V2',
  fullName: 'Monk Algo ORB V2',
  label: 'Growth',
  tagline: 'Twice the trades. Bigger swings.',
  description: 'Choose it when you want more opportunities and total return; size risk lower to stay inside your limit.',
  color: '#06B6D4', // Cyan
  colorLight: '#22D3EE',
  colorDark: '#0891B2',
  
  // 1-Year Performance (365 days)
  oneYear: {
    totalReturn: 73.58,
    netProfit: 73576.50,
    winRate: 71.97,
    profitFactor: 2.16,
    maxDrawdown: 3.34,
    maxDrawdownDollars: 5579.50,
    totalTrades: 239,
    winningTrades: 172,
    losingTrades: 67,
    grossProfit: 136907.50,
    grossLoss: 63331.00,
    expectancy: 307.85,
    largestProfit: 1000.00,
    largestLoss: 999.00,
    dateRange: 'Oct 2025 – Sep 2026',
  },
  
  // 5-Year Performance
  fiveYear: {
    totalTrades: 1149,
    dateRange: 'Dec 2021 – Sep 2026',
  },
  
  // Monte Carlo Pass Rates (50,000 simulations, 4% DD limit)
  monteCarlo: {
    simulations: 50000,
    ddLimit: 4,
    passRates: {
      0.25: { pass: 100.0, avgReturn: 50.49, avgDD: 2.94 },
      0.50: { pass: 98.9, avgReturn: 125.23, avgDD: 5.82 },
      0.75: { pass: 78.9, avgReturn: 235.23, avgDD: 8.63 },
      1.00: { pass: 33.2, avgReturn: 396.21, avgDD: 11.37 },
    },
  },
  
  // Recommended risk levels
  recommendedRisk: 0.50,
  defaultRisk: 0.50,
  
  // Monthly P&L (1-year) - Recalculated from V2 365 days CSV
  // Note: Sep 30 2025 trade ($814) included in Oct '25 for display purposes
  // Total: $73,576.50 matches net profit exactly
  monthlyPnl: [
    { month: "Oct '25", profit: 5780.50 },  // 4966.50 + 814 (Sep 30 trade)
    { month: "Nov '25", profit: 4026.50 },
    { month: "Dec '25", profit: 4095.50 },
    { month: "Jan '26", profit: 8381.00 },
    { month: "Feb '26", profit: 3215.50 },
    { month: "Mar '26", profit: 11213.50 },
    { month: "Apr '26", profit: 3070.00 },
    { month: "May '26", profit: 8060.50 },
    { month: "Jun '26", profit: 9444.00 },
    { month: "Jul '26", profit: 8985.00 },
    { month: "Aug '26", profit: 2440.00 },
    { month: "Sep '26", profit: 4864.50 },  // Fixed: was 3919.50
  ],
};

// ============================================
// DERIVED/AGGREGATE VALUES
// ============================================
export const COMBINED_STATS = {
  totalTrades1Year: V1_STATS.oneYear.totalTrades + V2_STATS.oneYear.totalTrades,
  totalTrades5Year: V1_STATS.fiveYear.totalTrades + V2_STATS.fiveYear.totalTrades,
  combinedReturn: V1_STATS.oneYear.totalReturn + V2_STATS.oneYear.totalReturn,
  avgWinRate: ((V1_STATS.oneYear.winRate + V2_STATS.oneYear.winRate) / 2).toFixed(1),
  lowestDrawdown: Math.min(V1_STATS.oneYear.maxDrawdown, V2_STATS.oneYear.maxDrawdown),
  highestProfitFactor: Math.max(V1_STATS.oneYear.profitFactor, V2_STATS.oneYear.profitFactor),
};

// ============================================
// ASSET URLs
// ============================================
export const ASSETS = {
  v1: {
    pdfReport: 'https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/aeq5exli_V1%20report.pdf',
    csv1Year: 'https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/9ohv0bw0_V1%20365%20days%20Upgrade.csv',
    csv5Year: 'https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/h47gxet4_V1_5_years_.csv',
    screenshots: {
      overview: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/w6vbvtc2_image.png",
      performance: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/s7lebkp8_image.png",
      trades: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/t7ngbhvy_image.png",
    },
  },
  v2: {
    pdfReport: 'https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/mlsicuj2_V2%20report.pdf',
    csv1Year: 'https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/m9yvw0te_V2%20365%20days%20Upgrade.csv',
    csv5Year: 'https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/posilcwf_V2_5_years_.csv',
    screenshots: {
      overview: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/4xc0bwgw_Screenshot%20%282988%29.png",
      performance: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/a8jf4sly_Screenshot%20%282989%29.png",
      trades: "https://customer-assets-0z36b82j.emergentagent.net/job_60ef5a15-9d39-4076-a479-d4706fc080a7/artifacts/3nz2cdh7_Screenshot%20%282990%29.png",
    },
  },
  tester: {
    downloadUrl: '#tester-section', // TODO: Add actual tester download URL
  },
};

// ============================================
// FIT FINDER CONFIGURATION
// ============================================
export const FIT_FINDER_CONFIG = {
  drawdownLimits: [3, 4, 5, 6],
  riskMultipliers: [
    { label: 'Lower (0.5×)', value: 0.5 },
    { label: 'Default (1×)', value: 1.0 },
    { label: 'Higher (1.5×)', value: 1.5 },
  ],
  // Base drawdowns at default risk (1×)
  baseDrawdowns: {
    V1: V1_STATS.oneYear.maxDrawdown,
    V2: V2_STATS.oneYear.maxDrawdown,
  },
};

// ============================================
// COPY & MESSAGING
// ============================================
export const COPY = {
  hero: {
    eyebrow: 'OPENING RANGE BREAKOUT · NAS100/MNQ · MT5 & NINJATRADER 8',
    headline: 'Pass challenges. Grow your capital. Both engines do both.',
    subhead: 'One ORB system, two variations — V1 trades fewer and calmer, V2 trades more and bigger. Both pass challenges and grow funded accounts; you pick the engine by style and the risk by your drawdown limit.',
    ctaPrimary: 'Get Access',
    ctaSecondary: 'Verify It — Free Tester',
  },
  fitFinder: {
    title: 'Which Engine Fits Your Account?',
    subtitle: 'Enter your drawdown limit and risk preference to see which variation fits.',
    verdict: {
      bothFit: 'Both engines fit your {limit}% limit at this risk level.',
      v1Only: 'V1 fits your {limit}% limit. Want V2? Lower the risk size until its drawdown clears the line.',
      v2Only: 'V2 fits your {limit}% limit. V1 also fits with room to spare.',
      neitherFit: 'Neither engine fits at this risk level. Try lowering the risk multiplier.',
    },
    note: 'Both engines work on any account. Drawdowns scale roughly with risk size — an approximation, not a separate backtest.',
  },
  engines: {
    sectionTitle: 'Meet the Two Engines',
    sectionSubtitle: 'Same strategy, different trade frequencies and risk profiles.',
  },
  monteCarlo: {
    callout: 'Notice: V2 at 0.5% risk and V1 at 0.75% risk both pass ~99% of simulations. Same outcome, two trading styles — the risk % is the dial.',
  },
  comparison: {
    v1BestFor: 'Traders who want the calmest ride',
    v2BestFor: 'Traders who want the most action & total return',
  },
};

// ============================================
// FAQ DATA
// ============================================
export const FAQ_DATA = [
  {
    question: "Which variation should I start with?",
    answer: `Either works on any account — challenge, funded, or personal. V1 trades fewer and dips less; V2 trades more and returns more. Running V1 at ~0.75% risk behaves similarly to V2 at ~0.5% risk in drawdown terms.`
  },
  {
    question: "What is Monk Algo ORB?",
    answer: `Monk Algo ORB is a professionally optimized Opening Range Breakout algorithm for NAS100/MNQ. It comes in two variations — V1 (Conservative) and V2 (Growth) — both included in one subscription. The difference is trade frequency and risk profile, not capability.`
  },
  {
    question: "Can I use both variations?",
    answer: `Yes! Your subscription includes both V1 and V2. You can run either variation on your accounts and switch anytime. Many traders use V1 on stricter drawdown accounts and V2 on accounts with more headroom.`
  },
  {
    question: "What platforms are supported?",
    answer: `The algorithm runs on MetaTrader 5 (MT5) as an Expert Advisor and on NinjaTrader 8 as a strategy. Your subscription includes both platform versions and 2 activations.`
  },
  {
    question: "Is this suitable for prop firm challenges?",
    answer: `Yes. Both V1 and V2 are suitable for prop firm challenges. V1's lower drawdown gives more safety margin, while V2's higher trade count can reach profit targets faster. Use the Monte Carlo tables to see pass probabilities at different risk levels.`
  },
  {
    question: "Are these live trading results?",
    answer: `No. All performance data comes from backtests on historical NAS100/MNQ data. Past performance, backtested or live, does not guarantee future results. We provide a free NinjaTrader 8 tester so you can run the backtest yourself before subscribing, and you can watch the full 2022–2026 backtest recorded live with no cuts.`
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================
export const getStats = (version) => {
  return version === 'V1' ? V1_STATS : V2_STATS;
};

export const formatNumber = (num, decimals = 2) => {
  return num.toLocaleString('en-US', { 
    minimumFractionDigits: decimals, 
    maximumFractionDigits: decimals 
  });
};

export const formatPercent = (num) => {
  return `${num.toFixed(2)}%`;
};

export const formatCurrency = (num) => {
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Interpolate Monte Carlo pass rate for custom risk values
export const interpolatePassRate = (version, riskPct) => {
  const stats = getStats(version);
  const passRates = stats.monteCarlo.passRates;
  const knownRisks = Object.keys(passRates).map(Number).sort((a, b) => a - b);
  
  if (riskPct <= knownRisks[0]) return passRates[knownRisks[0]];
  if (riskPct >= knownRisks[knownRisks.length - 1]) return passRates[knownRisks[knownRisks.length - 1]];
  
  let lower = knownRisks[0], upper = knownRisks[knownRisks.length - 1];
  for (let i = 0; i < knownRisks.length - 1; i++) {
    if (riskPct >= knownRisks[i] && riskPct <= knownRisks[i + 1]) {
      lower = knownRisks[i];
      upper = knownRisks[i + 1];
      break;
    }
  }
  
  const t = (riskPct - lower) / (upper - lower);
  const lowerData = passRates[lower];
  const upperData = passRates[upper];
  
  return {
    pass: Math.round((lowerData.pass + t * (upperData.pass - lowerData.pass)) * 10) / 10,
    avgReturn: Math.round((lowerData.avgReturn + t * (upperData.avgReturn - lowerData.avgReturn)) * 100) / 100,
    avgDD: Math.round((lowerData.avgDD + t * (upperData.avgDD - lowerData.avgDD)) * 100) / 100,
  };
};

export default {
  V1_STATS,
  V2_STATS,
  COMBINED_STATS,
  ASSETS,
  FIT_FINDER_CONFIG,
  COPY,
  FAQ_DATA,
  getStats,
  formatNumber,
  formatPercent,
  formatCurrency,
  interpolatePassRate,
};

// YouTube videos on the ORB page (full URLs)
export const ORB_VIDEOS = {
  liveBacktest: 'https://youtu.be/5Atd959RadQ',
  nt8Guide: 'https://youtu.be/EyzVPqkJl4M',
  thumbVersion: '2', // bump after changing a thumbnail on YouTube so browsers fetch the new one
};
